import { Chip, Typography, Tooltip, Box } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { formatMoney } from "../../../referencias-tablas/clientes/datoscliente/cuentacorriente/utils/formatMoney";

// 🚦 Indicador visual del semáforo
const SemaforoDot = ({ semaforo }) => {
  const config = {
    ROJO: {
      color: "#ef4444",
      glow: "#fee2e2",
      icon: <ErrorOutlineIcon sx={{ fontSize: 14, color: "white" }} />,
      label: "Vencido",
    },
    AMARILLO: {
      color: "#f59e0b",
      glow: "#fef3c7",
      icon: <WarningAmberIcon sx={{ fontSize: 14, color: "white" }} />,
      label: "Próximo a vencer",
    },
    BLANCO: {
      color: "#22c55e",
      glow: "#dcfce7",
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 14, color: "white" }} />,
      label: "Al día",
    },
  };

  const c = config[semaforo] || config.BLANCO;

  return (
    <Tooltip title={c.label} arrow placement="right">
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          bgcolor: c.color,
          boxShadow: `0 0 0 4px ${c.glow}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            transform: "scale(1.15)",
            boxShadow: `0 0 0 6px ${c.glow}`,
          },
        }}
      >
        {c.icon}
      </Box>
    </Tooltip>
  );
};

// 📊 Badge de días de atraso
const DiasBadge = ({ dias }) => {
  let config = {
    bgcolor: "#f0fdf4",
    color: "#16a34a",
    border: "#bbf7d0",
  };

  if (dias > 90) {
    config = { bgcolor: "#fef2f2", color: "#dc2626", border: "#fecaca" };
  } else if (dias > 30) {
    config = { bgcolor: "#fffbeb", color: "#d97706", border: "#fde68a" };
  } else if (dias > 7) {
    config = { bgcolor: "#fff7ed", color: "#ea580c", border: "#fed7aa" };
  } else if (dias > 0) {
    config = { bgcolor: "#fefce8", color: "#ca8a04", border: "#fde047" };
  }

  return (
    <Chip
      label={`${dias} ${dias === 1 ? "día" : "días"}`}
      size="small"
      sx={{
        bgcolor: config.bgcolor,
        color: config.color,
        border: `1px solid ${config.border}`,
        fontWeight: 700,
        fontSize: 11,
        height: 26,
        borderRadius: 3,
        "& .MuiChip-label": { px: 1.2 },
      }}
    />
  );
};

// 💰 Celda de monto
const MoneyCell = ({ value, bold = false, color = "text.primary" }) => (
  <Typography
    sx={{
      fontSize: 13,
      fontWeight: bold ? 700 : 500,
      color,
      fontFamily: '"SF Mono", "Fira Code", "Roboto Mono", monospace',
      letterSpacing: "-0.2px",
      textAlign: "right",
    }}
  >
    {formatMoney(value)}
  </Typography>
);

export const buildColumnsSemaforo = () => [
  {
    accessorKey: "semaforo",
    header: "",
    size: 60,
    enableSorting: true,
    muiTableHeadCellProps: { align: "center" },
    muiTableBodyCellProps: { align: "center" },
    Cell: ({ cell }) => <SemaforoDot semaforo={cell.getValue()} />,
  },
  {
    accessorKey: "id",
    header: "ID",
    size: 80,
    muiTableHeadCellProps: { align: "left" },
    Cell: ({ cell }) => (
      <Typography
        sx={{
          fontSize: 12,
          color: "#64748b",
          fontFamily: '"SF Mono", "Fira Code", "Roboto Mono", monospace',
          fontWeight: 500,
          letterSpacing: "0.5px",
        }}
      >
        {cell.getValue()}
      </Typography>
    ),
    Footer: () => (
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 800,
          color: "#475569",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        Totales
      </Typography>
    ),
  },
  {
    accessorKey: "razon_social",
    header: "Razón Social",
    minSize: 280,
    Cell: ({ cell, row }) => {
      const semaforo = row.original.semaforo;
      const dotColor = {
        ROJO: "#ef4444",
        AMARILLO: "#f59e0b",
        BLANCO: "#22c55e",
      }[semaforo];

      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: dotColor,
              flexShrink: 0,
              opacity: 0.6,
            }}
          />
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#0f172a",
              letterSpacing: "-0.2px",
            }}
          >
            {cell.getValue()}
          </Typography>
        </Box>
      );
    },
  },
  {
    accessorKey: "nombre_sucursal",
    header: "Sucursal",
    size: 100,
    Cell: ({ cell }) => (
      <Typography sx={{ fontSize: 13, color: "#64748b" }}>
        {cell.getValue()}
      </Typography>
    ),
  },
  {
    accessorKey: "dias_mayor_atraso",
    header: "Días atraso",
    size: 120,
    muiTableHeadCellProps: { align: "center" },
    muiTableBodyCellProps: { align: "center" },
    Cell: ({ cell }) => <DiasBadge dias={cell.getValue()} />,
  },
  {
    accessorKey: "vencidos",
    header: "Vencidos",
    size: 140,
    muiTableBodyCellProps: { align: "right" },
    muiTableHeadCellProps: { align: "right" },
    Cell: ({ cell }) => (
      <MoneyCell
        value={cell.getValue()}
        color={cell.getValue() > 0 ? "#dc2626" : "#94a3b8"}
        bold={cell.getValue() > 0}
      />
    ),
    Footer: ({ table }) => (
      <MoneyCell
        value={table.options.meta?.totales?.vencidos || 0}
        color="#dc2626"
        bold
      />
    ),
  },
  {
    accessorKey: "no_vencidos",
    header: "No vencidos",
    size: 140,
    muiTableBodyCellProps: { align: "right" },
    muiTableHeadCellProps: { align: "right" },
    Cell: ({ cell }) => <MoneyCell value={cell.getValue()} />,
    Footer: ({ table }) => (
      <MoneyCell value={table.options.meta?.totales?.noVencidos || 0} bold />
    ),
  },
  {
    accessorKey: "total_cta_cte",
    header: "Total Cta. Cte.",
    size: 160,
    muiTableBodyCellProps: { align: "right" },
    muiTableHeadCellProps: { align: "right" },
    Cell: ({ cell }) => <MoneyCell value={cell.getValue()} bold />,
    Footer: ({ table }) => (
      <MoneyCell
        value={table.options.meta?.totales?.totalCtaCte || 0}
        bold
        color="#0f172a"
      />
    ),
  },
  {
    accessorKey: "total_cheques",
    header: "Cheques",
    size: 140,
    muiTableBodyCellProps: { align: "right" },
    muiTableHeadCellProps: { align: "right" },
    Cell: ({ cell }) => <MoneyCell value={cell.getValue()} />,
  },
  {
    accessorKey: "total",
    header: "Total",
    size: 160,
    muiTableBodyCellProps: { align: "right" },
    muiTableHeadCellProps: { align: "right" },
    Cell: ({ cell }) => (
      <MoneyCell value={cell.getValue()} bold color="#0f172a" />
    ),
  },
];
